/**
 * Why a held position rendered without a price — named, in the browser, at the
 * moment it happens.
 *
 * The operator's report: buy a token that was not in the portfolio, and both
 * screens show "—" for about a second before the number appears. Half of that
 * is proven and fixed — the hook used to DROP the tick for a token the wallet
 * did not hold yet, which is exactly the token just bought. The other half is
 * not proven: whether canon can answer with `price_available: false` for a
 * position we could not price from the tape either, and for how long.
 *
 * Guessing that would repeat the mistake this session already made once. So
 * this states it instead, from the two facts that separate the cases:
 *
 *   canon unpriced + store HAS a price  -> nothing on screen shows a dash; the
 *                                          store covered the window.
 *   canon unpriced + store has nothing  -> this is the dash the operator sees,
 *                                          and its cause is upstream of the
 *                                          browser (the backend's own
 *                                          `portfolio_unpriced` line names
 *                                          which half was missing).
 *
 * Reads CANON's holdings, deliberately: the repriced copy has already flipped
 * `price_available` wherever the store helped, so by then the distinction is
 * gone. Never throws, never gates anything, and says nothing when every
 * position is priced — which is the normal state.
 */

import type { WireHolding } from '../api/portfolio'
import { getLivePrice, onLivePriceStoreReset } from './livePriceStore'

export type UnpricedSighting = {
  token: string
  balance: string
  /** The store had a live price, so the row was priced despite canon. */
  covered: boolean
  /** Age of that price in seconds at the moment of the sighting; null when
      there was none. */
  priceAgeSec: number | null
}

/** A ceiling on the noise: a wallet full of unpriceable dust must not fill the
    console and bury the one line that matters. */
const MAX_SIGHTINGS = 20

const reported = new Set<string>()
let emitted = 0

function positiveBalance(raw: string | undefined): boolean {
  if (typeof raw !== 'string' || raw.trim() === '') return false
  const value = Number(raw)
  return Number.isFinite(value) && value > 0
}

/**
 * Offers canon's holdings. Each (token, covered) pair is reported once per
 * session: the question is which STATES occur, not how many renders occur.
 *
 * @param nowSec Read time, for the price age. Injected so the test states it.
 */
export function noteUnpricedHoldings(
  holdings: readonly WireHolding[] | null | undefined,
  nowSec: number = Math.floor(Date.now() / 1000)
): UnpricedSighting[] {
  try {
    if (!Array.isArray(holdings)) return []
    return holdings
      .filter(isDashCandidate)
      .map((holding) => sight(holding, nowSec))
      .filter(isNew)
      .map(report)
  } catch {
    // A diagnostic that can break a render is worse than no diagnostic.
    return []
  }
}

function isDashCandidate(holding: WireHolding): boolean {
  // A position the projector refused to account for carries no figures by
  // design; its dash is honest and is not this window.
  if (holding.price_available || holding.position_unavailable) return false
  return positiveBalance(holding.balance)
}

function sight(holding: WireHolding, nowSec: number): UnpricedSighting {
  const price = getLivePrice(holding.token_master)
  return {
    token: holding.token_master,
    balance: holding.balance,
    covered: price !== undefined,
    priceAgeSec: price === undefined ? null : nowSec - Math.floor(price.knownAsOfMs / 1000),
  }
}

function isNew(sighting: UnpricedSighting): boolean {
  const key = `${sighting.token}:${sighting.covered}`
  if (reported.has(key) || emitted >= MAX_SIGHTINGS) return false
  reported.add(key)
  emitted += 1
  return true
}

function report(sighting: UnpricedSighting): UnpricedSighting {
  console.info(
    `[wils.unpriced] canon returned no price for ${sighting.token}`,
    sighting.covered
      ? `— the live store covered it (price ${sighting.priceAgeSec}s old), no dash was shown`
      : '— the store had none either, so this is the dash on screen'
  )
  return sighting
}

/** Test seam: module state outlives a test file. */
export function resetUnpricedHoldingDiagForTest(): void {
  reported.clear()
  emitted = 0
}

// This ledger is only meaningful alongside the store it reads, and any test
// that mounts the provider spends from its ceiling. Resetting the store resets
// this too, so a later test file in the same worker does not inherit a budget
// someone else already used.
onLivePriceStoreReset(resetUnpricedHoldingDiagForTest)
